import { useCallback } from 'react';
import { FieldValues, Path, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import CheckCircle from '@mui/icons-material/CheckCircle';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { Stack, StackProps } from '@mui/material';

import { yupResolver } from '@hookform/resolvers/yup';

import { notificationService } from '@arrived/common';
import { emailListSubscriptionSchema, EmailListSubscriptionSchema } from '@arrived/forms';
import { EmailSubscribeMutationRequest, useEmailSubscribeMutation } from '@arrived/queries';

import { FormTextField, FormTextFieldProps } from '../form';

export interface SubscribeProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> extends StackProps {
  buttonProps?: LoadingButtonProps;
  formTextFieldProps?: Omit<FormTextFieldProps<TFieldValues, TName>, 'controllerProps'>;
  onSubscribe?: (email: string) => void;
}

export const Subscribe = <TFieldValues extends FieldValues, TName extends Path<TFieldValues>>({
  buttonProps,
  formTextFieldProps,
  onSubscribe,
  sx,
  ...rest
}: SubscribeProps<TFieldValues, TName>) => {
  const { control, formState, handleSubmit } = useForm<EmailListSubscriptionSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(emailListSubscriptionSchema),
  });

  const { errors } = formState;
  const intl = useIntl();

  const { sx: formTextFieldPropsSx, ...formTextFieldPropsRest } = formTextFieldProps || {};
  const { children: buttonPropsChildren, sx: buttonPropsSx, ...buttonPropsRest } = buttonProps || {};

  const {
    mutate: postEmailSubscribe,
    isLoading,
    isSuccess,
  } = useEmailSubscribeMutation({
    onSuccess: (_, { email }) => {
      notificationService.success(
        <FormattedMessage id="thank-you-for-subscribing" defaultMessage="Thank you for subscribing!" />
      );
      onSubscribe?.(email);
    },
    onError: () =>
      notificationService.error(
        <FormattedMessage id="email-subscription-error" defaultMessage="Error subscribing to mailing list" />
      ),
  });

  const handleOnSubmit = useCallback(
    (values: EmailSubscribeMutationRequest) => postEmailSubscribe(values),
    [postEmailSubscribe]
  );

  const StackFormComponent = useCallback(
    (props: any) => <form onSubmit={handleSubmit(handleOnSubmit)} {...props} />,
    [handleSubmit, handleOnSubmit]
  );

  return (
    <Stack
      alignItems="stretch"
      component={StackFormComponent}
      direction="row"
      borderRadius={1}
      overflow="hidden"
      height="fit-content"
      position="relative"
      sx={{ backgroundColor: 'white', ...sx }}
      {...rest}
    >
      <FormTextField
        id="email"
        controllerProps={{ control, name: 'email' }}
        error={Boolean(errors.email)}
        placeholder={intl.formatMessage({ id: 'email', defaultMessage: 'Email' })}
        variant="filled"
        type="email"
        sx={{
          backgroundColor: 'white',
          minWidth: '170px',
          flex: '1 1 auto',
          '& .MuiInputBase-input': { backgroundColor: 'white', px: 2, py: 1.5 },
          '& .MuiInputBase-root::before': { display: 'none' },
          ...formTextFieldPropsSx,
        }}
        {...formTextFieldPropsRest}
      />
      <LoadingButton
        color="primary"
        disabled={isSuccess}
        loading={isLoading}
        type="submit"
        sx={{
          height: 'unset',
          width: 'auto',
          minWidth: '150px',
          minHeight: 'unset',
          px: 2,
          py: 1.5,
          '&.Mui-disabled': {
            backgroundColor: 'dark.200',
          },
          borderBottomLeftRadius: 0,
          borderTopLeftRadius: 0,
          ...buttonPropsSx,
        }}
        variant="contained"
        {...buttonPropsRest}
      >
        <Stack direction="row" gap={0.5}>
          {isSuccess ? (
            <>
              <CheckCircle />
              <FormattedMessage id="youre-set" defaultMessage="You're set!" />
            </>
          ) : (
            <>{buttonPropsChildren ?? <FormattedMessage id="get-started" defaultMessage="Get started" />}</>
          )}
        </Stack>
      </LoadingButton>
    </Stack>
  );
};
